import { useEffect, useState } from "react";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  IconButton,
  Icon,
  Typography,
  Modal,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import { contasAReceberService } from "./service/criar-contas-a-receber.service";
import { useLoader } from "../../shared/contexts/LoaderContext";

interface Parcela {
  id: string;
  description: string;
  value: string | number;
  status: string;
  dueDate: string;
  paymentDate?: string | null;
}

interface ContaAPagar {
  id: string;
  description: string;
  totalValue: string | number;
  status: string;
  anticipateAccount: boolean;
  paidInstallments: number;
  openInstallments: number;
  installments?: Parcela[];
}

const statusOptions = [
  { value: "open", label: "Aberta" },
  { value: "closed", label: "Fechada" },
];

function ModalFiltrosContasAPagar({
  open,
  onClose,
  onFiltrar,
}: {
  open: boolean;
  onClose: () => void;
  onFiltrar: (filters: any) => void;
}) {
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);

  const [dateStart, setDateStart] = useState(dateInicio);
  const [dateEnd, setDateEnd] = useState(today);
  const [status, setStatus] = useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltrar({
      dateStart,
      dateEnd,
      status,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          maxWidth: 400,
          margin: "80px auto",
          padding: 3,
          outline: "none",
        }}
      >
        <Typography variant="h6" mb={2}>
          Filtros de Contas a Receber
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Data Inicial"
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Data Final"
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Status"
                select
                value={status}
                onChange={(e) =>
                  setStatus(
                    Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value]
                  )
                }
                fullWidth
                SelectProps={{
                  multiple: true,
                }}
                InputLabelProps={{ shrink: true }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="contained" type="submit">
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
}

export function ListarContasAReceber() {
  const [lista, setLista] = useState<ContaAPagar[]>([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<any>({});
   const { showLoader, hideLoader } = useLoader();

  const navigate = useNavigate();

  useEffect(() => {
    carregarLista(filters, page, rowsPerPage);
    // eslint-disable-next-line
  }, [filters, page, rowsPerPage]);

  const carregarLista = async (filters: any, pagina = 0, limite = 10) => {
    try {
      showLoader();
      const query = montarQuery(filters);
      const result = await contasAReceberService.findAll(query, pagina, limite);
      setLista(result.items || []);
      setTotalCount(result.totalItems || 0);
    } catch (error) {
      setLista([]);
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta obra?")) {
      try {
        const obraDeletada = await contasAReceberService.deletar(id);
        toast.success(obraDeletada);
      } catch (error) {
        toastError(error);
      }
    }
  };

  const montarQuery = (filters: any) => {
    let query = "";
    if (filters.dateStart && filters.dateEnd) {
      query += `campo_pesquisa=purchase_date&tipo_pesquisa=entre&valor_pesquisa=${filters.dateStart},${filters.dateEnd}&`;
    }
    if (filters.status && filters.status.length) {
      query += `campo_pesquisa=status&tipo_pesquisa=in&valor_pesquisa=${filters.status.join(
        ","
      )}&`;
    }
    query = query.replace(/&$/, "");
    return query;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleOpenModal = () => setOpen(true);

  const handleFiltrar = (newFilters: any) => {
    setFilters(newFilters);
    setPage(0);
    setOpen(false);
  };

  return (
    <BaseLayout
      title="Contas a Pagar"
      barraDeFerramentas={
        <FerramanetasDaListagem
          textoBotaoNovo="Nova Contas a Receber"
          mostrarInputBusca={false}
          mostraBotaoFiltro={true}
          aoClicarBotaoFiltro={handleOpenModal}
          aoClicarBotaoNovo={() => navigate("/financeiro/contas-a-receber/criar")}
        />
      }
    >
      <ModalFiltrosContasAPagar
        open={open}
        onClose={() => setOpen(false)}
        onFiltrar={handleFiltrar}
      />

      <Box height="100%">
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
        {lista.length > 0 ? (
          lista.map((conta) => (
            <Accordion key={conta.id} sx={{ mb: 2, width: "100%", '@media (max-width:599px)': { width: '600px', overflowX: 'auto' } }}>
              <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                <Box display="flex" flexDirection="column" width="100%">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {conta.description}
                  </Typography>
                  <Box display="flex" gap={2} alignItems="center" mt={0.5}>
                    <Typography variant="body2">
                      Valor Total:{" "}
                      <strong>
                        {Number(conta.totalValue).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                          minimumFractionDigits: 2,
                        })}
                      </strong>
                    </Typography>
                    <Chip
                      label={conta.status === "open" ? "Aberta" : "Fechada"}
                      color={conta.status === "open" ? "success" : "default"}
                      size="small"
                    />
                    {conta.anticipateAccount ? (
                      <Chip
                        label="Boleto de Antecipação"
                        color="primary"
                        size="small"
                      />
                    ) : Array.isArray(conta.installments) &&
                      conta.installments.some(
                        (p) => p.status === "anticipated"
                      ) ? (
                      <Chip label="Antecipado" color="primary" size="small" />
                    ) : null}
                    <Typography variant="body2">
                      Pagas: <strong>{conta.paidInstallments}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Abertas: <strong>{conta.openInstallments}</strong>
                    </Typography>
                    {/* Ícones de ação */}
                    <Box ml={2} display="flex" gap={1}>
                      {conta.status != "open" ||
                        (!conta.anticipateAccount && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(
                                  `/financeiro/contas-a-receber/detalhe/${conta.id}`
                                )
                              }
                              title="Visualizar documento"
                            >
                              <Icon>visibility</Icon>
                            </IconButton>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                handleDelete(conta.id);
                              }}
                              title="Excluir documento"
                            >
                              <Icon>delete</Icon>
                            </IconButton>
                          </>
                        ))}
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" mb={1}>
                  Parcelas
                </Typography>
                {Array.isArray(conta.installments) &&
                  conta.installments.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Valor</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Vencimento</TableCell>
                        <TableCell>Pagamento</TableCell>
                        <TableCell>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conta.installments.map((parcela) => (
                        <TableRow key={parcela.id}>
                          <TableCell>{parcela.description}</TableCell>
                          <TableCell>
                            {Number(parcela.value).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                              minimumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Verifica se está atrasado (status pending e data vencimento menor que hoje)
                              const isOverdue =
                                parcela.status === "pending" &&
                                parcela.dueDate &&
                                new Date(parcela.dueDate) < new Date();

                              if (isOverdue) {
                                return (
                                  <Chip
                                    label="Em atraso"
                                    color="error"
                                    size="small"
                                  />
                                );
                              }

                              switch (parcela.status) {
                                case "pending":
                                  return (
                                    <Chip
                                      label="Aguardando pagamento"
                                      color="info"
                                      size="small"
                                    />
                                  );
                                case "paid":
                                  return (
                                    <Chip
                                      label="Paga"
                                      color="success"
                                      size="small"
                                    />
                                  );
                                case "overdue":
                                  return (
                                    <Chip
                                      label="Atrasada"
                                      color="error"
                                      size="small"
                                    />
                                  );
                                case "anticipated":
                                  return (
                                    <Chip
                                      label="Antecipada"
                                      color="primary"
                                      size="small"
                                    />
                                  );
                                default:
                                  return (
                                    <Chip
                                      label={parcela.status}
                                      color="default"
                                      size="small"
                                    />
                                  );
                              }
                            })()}
                          </TableCell>
                          <TableCell>
                            {parcela.dueDate
                              ? new Date(parcela.dueDate).toLocaleDateString(
                                "pt-BR"
                              )
                              : "--"}
                          </TableCell>
                          <TableCell>
                            {parcela.paymentDate
                              ? new Date(
                                parcela.paymentDate
                              ).toLocaleDateString("pt-BR")
                              : "--"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Icon>payments</Icon>}
                              onClick={() =>
                                navigate(
                                  `/financeiro/contas-a-receber/liquidar/${parcela.id}`,
                                  {
                                    state: {
                                      parcela,
                                      conta,
                                    },
                                  }
                                )
                              }
                              disabled={
                                parcela.status === "paid" ||
                                parcela.status === "anticipated"
                              }
                            >
                              Liquidar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma parcela encontrada.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <RetornoDaListagemVazia />
        )}
      </Box>
    </BaseLayout>
  );
}
